<script setup lang="ts">
import Button from 'primevue/button';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface Props {
  variant?: Variant;
  label?: string;
  loading?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  label: '',
  loading: false,
  disabled: false,
  type: 'button',
});

// Static class strings per variant. Tailwind's source scanner sees every class
// name as a literal in this file (libs/ui/src/**/*.vue is in @source scope).
// CRITICAL: never interpolate fragments — every class must be a complete name.
const VARIANT_CLASSES: Record<Variant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-hover focus:ring-primary',
  secondary:
    'bg-surface border border-border-strong text-foreground hover:bg-canvas focus:ring-border-strong',
  ghost:
    'bg-transparent text-primary hover:bg-primary-light focus:ring-primary',
  danger: 'bg-danger text-white hover:bg-red-700 focus:ring-danger',
};

// Function-based pt.root MERGES with the global pt.button.root from
// apps/customer-portal/src/plugins/primevue.ts (ptOptions.mergeSections=true).
// We append variant + state classes to the global default base.
const buttonPt = {
  root: () => {
    const variantCls = VARIANT_CLASSES[props.variant];
    const stateCls =
      props.disabled || props.loading
        ? 'opacity-50 cursor-not-allowed pointer-events-none'
        : '';
    return `inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium min-h-[44px] sm:min-h-[40px] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 ${variantCls} ${stateCls}`.trim();
  },
};
</script>

<template>
  <Button
    :pt="buttonPt"
    :disabled="disabled || loading"
    :aria-busy="loading ? 'true' : undefined"
    :type="type"
  >
    <template #default>
      <span v-if="loading" class="inline-flex items-center">
        <FontAwesomeIcon :icon="faSpinner" class="animate-spin mr-2 size-4" />
        <span class="sr-only">Loading, please wait</span>
      </span>
      <span v-else
        ><slot>{{ label }}</slot></span
      >
    </template>
  </Button>
</template>
